import { createContext, type ReactNode, useContext, useState } from "react";

import { IS_DEBUG_TOOLS_ENABLED } from "~/constants/envConstants.ts";
import { useAuth } from "~/contexts/AuthContext";
import { useAllUsers } from "~/modules/user/api/user/userQueries.ts";

type DebugUserContextValue = {
	debugUserId: string | null;
	setDebugUserId(userId: string | null): void;
};

const DebugUserContext = createContext<DebugUserContextValue>({ debugUserId: null, setDebugUserId: () => {} });

// Holds the debug user in state, not in the URL: a personal dashboard has no user id in its address.
export function DashboardUserProvider({ children }: { children: ReactNode }) {
	const [debugUserId, setDebugUserId] = useState<string | null>(null);

	return (
		<DebugUserContext.Provider value={{ debugUserId, setDebugUserId }}>
			{children}
		</DebugUserContext.Provider>
	);
}

export function useDebugUserSelection() {
	return useContext(DebugUserContext);
}

// The user whose data the dashboard shows. This is the signed-in user, except where the debug tools
// are on and the picker names another user. The backend still decides what the signed-in user may read.
export function useDashboardUser() {
	const { user } = useAuth();
	const { data: users } = useAllUsers();
	const { debugUserId } = useDebugUserSelection();

	const debugUser = IS_DEBUG_TOOLS_ENABLED && debugUserId
		? users?.find((candidate) => candidate.id === debugUserId)
		: undefined;

	return debugUser ?? user;
}
